import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Button, Chip, Container, IconButton, Tooltip, Typography } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import { useAuth } from '../context/AuthContext';
import CommandPalette from './CommandPalette';
import { APP_BAR_HEIGHT } from './layout';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');

const AppShell = ({ children, maxWidth = 'lg' }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // The chip is a discoverability affordance for the shortcut — dispatching the
  // same keystroke keeps the open logic in one place, inside CommandPalette.
  const openPalette = () =>
    window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f6fa' }}>
      <Box
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid #e0e0e0',
          px: 4,
          height: APP_BAR_HEIGHT,
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}
      >
        <Box
          component={Link}
          to={user?.role === 'external' ? '/quota-monitoring' : '/'}

          display="flex"
          alignItems="center"
          gap={1.5}
          sx={{ textDecoration: 'none', color: 'inherit' }}
        >
          <img src="/logo.png" alt="Ranger" style={{ width: 32, height: 32 }} />
          <Typography variant="h6" fontFamily="'Work Sans', sans-serif" fontWeight={700}>
            Ranger
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Tooltip title={`Search (${isMac ? '⌘' : 'Ctrl+'}K)`}>
            <Chip
              icon={<SearchIcon sx={{ fontSize: 16 }} />}
              label={isMac ? '⌘K' : 'Ctrl K'}
              size="small"
              variant="outlined"
              onClick={openPalette}
              sx={{ fontFamily: "'Work Sans', sans-serif", cursor: 'pointer' }}
            />
          </Tooltip>
          <Chip
            label={`${user?.username} · ${user?.role}`}
            size="small"
            variant="outlined"
            sx={{ fontFamily: "'Work Sans', sans-serif" }}
          />
          {user?.role === 'stack' && (
            <Tooltip title="Partners">
              <IconButton size="small" component={Link} to="/partners" aria-label="Partners">
                <GroupRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {user?.role === 'stack' && (
            <Tooltip title="Settings">
              <IconButton size="small" component={Link} to="/settings">
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Button
            size="small"
            startIcon={<LogoutIcon />}
            onClick={() => { logout(); navigate('/login'); }}
            sx={{ textTransform: 'none' }}
          >
            Sign out
          </Button>
        </Box>
      </Box>
      <Container maxWidth={maxWidth} sx={{ py: 4 }}>
        {children}
      </Container>
      <CommandPalette />
    </Box>
  );
};

export default AppShell;
